import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircleQuestion, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";

export const QAPanel = ({ companyId }) => {
  const [questions, setQuestions] = useState([]);
  const [question, setQuestion] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/portal/companies/${companyId}/questions`); setQuestions(data); }
    catch (e) { console.error("Failed to load questions", e); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [companyId]);

  const ask = async () => {
    if (!question.trim()) return;
    setSending(true);
    try {
      await api.post(`/portal/companies/${companyId}/questions`, { question: question.trim() });
      toast.success("Question sent to the founder");
      setQuestion("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send question"); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="investor-qa-panel">
      <h2 className="mb-3 flex items-center gap-2 h-display text-lg font-semibold text-foreground"><MessageCircleQuestion className="h-5 w-5 text-primary" /> Ask the founder</h2>
      <Card className="rounded-xl border bg-card p-4">
        <div className="flex gap-2">
          <Textarea value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Ask about metrics, plans, runway…" rows={2} className="flex-1" data-testid="qa-question-input" />
          <Button onClick={ask} disabled={sending || !question.trim()} className="gap-1.5 self-end" data-testid="qa-question-send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Ask
          </Button>
        </div>
      </Card>
      {questions.length > 0 && (
        <div className="mt-3 space-y-2">
          {questions.map((q) => (
            <Card key={q.id} className="rounded-xl border bg-card p-4" data-testid={`qa-item-${q.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm font-semibold text-foreground">{q.question}</div>
                {q.status === "answered"
                  ? <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700">Answered</Badge>
                  : <Badge className="border-amber-200 bg-amber-50 text-amber-800">Pending</Badge>}
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">{formatDate(q.created_at)}</div>
              {q.answer && <div className="mt-2 rounded-md bg-secondary/60 p-3 text-sm text-foreground">{q.answer}</div>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
